/**
 * Account backup builder — Admin-only.
 *
 * Gathers every account-owned dataset for the current company and bundles it
 * into a single ZIP of CSV files. This is the mandatory pre-reset snapshot for
 * the "Full Account Data Reset" flow: it captures everything that will be
 * deleted (production tasks / timers / cost-time history, audit logs) plus the
 * reference data that is preserved (employees, labour rates) so the workspace
 * can be reconstructed or analysed later.
 *
 * Every query is scoped by `companyId` — the same tenant boundary enforced by
 * the Firestore rules — so a backup can only ever contain the caller's own
 * company data.
 *
 * Never imported by production screens.
 */

import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import { buildCSV, csvDateTime } from './csv';
import { buildProductionTasksCSV, buildWorkerPerformanceCSV } from './exportHelpers';
import { makeZip, downloadBlob } from './zip';

const BOM = '﻿';

async function fetchCompanyDocs(collectionName, companyId) {
  const snap = await getDocs(
    query(collection(db, collectionName), where('companyId', '==', companyId))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Flatten an activity-log entry's before/after payloads into a compact JSON string. */
function jsonCell(value) {
  if (value == null) return '';
  try { return JSON.stringify(value); } catch { return ''; }
}

function buildAuditLogCSV(logs) {
  const headers = ['Log ID', 'Timestamp', 'User ID', 'Action', 'Entity Type', 'Entity ID', 'Before', 'After'];
  const rows = logs.map((l) => [
    l.id,
    csvDateTime(l.createdAt),
    l.userId || '',
    l.action || '',
    l.entityType || '',
    l.entityId || '',
    jsonCell(l.before),
    jsonCell(l.after),
  ]);
  return buildCSV(headers, rows);
}

function buildEmployeesCSV(users, floorWorkers) {
  const headers = ['Type', 'ID', 'Name', 'Email', 'Role', 'Status', 'Active'];
  const rows = [
    ...users.map((u) => [
      'User account', u.id, u.displayName || '', u.email || '', u.role || '', u.status || '', '',
    ]),
    ...floorWorkers.map((w) => [
      'Floor worker', w.id, w.name || '', '', '', '', w.active === false ? 'No' : 'Yes',
    ]),
  ];
  return buildCSV(headers, rows);
}

function buildLabourRatesCSV({ users, userRates, floorWorkers, floorWorkerRates, adminSettings }) {
  const userName = (id) => users.find((u) => u.id === id)?.displayName || '';
  const workerName = (id) => floorWorkers.find((w) => w.id === id)?.name || '';

  const headers = ['Type', 'ID', 'Name', 'Hourly Rate', 'Currency'];
  const rows = [];

  if (adminSettings?.defaultTeamHourlyRate != null) {
    rows.push(['Default team rate', '', 'Default team hourly rate', adminSettings.defaultTeamHourlyRate, adminSettings.labourCostCurrency || '']);
  }
  for (const r of userRates) {
    rows.push(['User rate', r.id, userName(r.id), r.hourlyRate ?? '', r.currency || '']);
  }
  for (const r of floorWorkerRates) {
    rows.push(['Floor worker rate', r.id, workerName(r.id), r.hourlyRate ?? '', r.currency || '']);
  }
  return buildCSV(headers, rows);
}

function buildAccountInfoCSV(meta) {
  const headers = ['Field', 'Value'];
  const rows = [
    ['Company name', meta.companyName],
    ['Company ID', meta.companyId],
    ['Backup generated at', csvDateTime(meta.generatedAt)],
    ['Production tasks', meta.taskCount],
    ['Active tasks (running timers)', meta.activeTaskCount],
    ['Audit log entries', meta.auditCount],
    ['User accounts', meta.userCount],
    ['Floor workers', meta.floorWorkerCount],
    ['Note', 'CSV backup taken before Full Account Data Reset. Tasks and audit logs were deleted; users, roles, rates and settings were preserved.'],
  ];
  return buildCSV(headers, rows);
}

function slugify(name) {
  return (name || 'account')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'account';
}

function backupFilename(companyName, date) {
  const p = (n) => String(n).padStart(2, '0');
  const stamp = `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}-${p(date.getHours())}${p(date.getMinutes())}`;
  return `account-reset-backup-${slugify(companyName)}-${stamp}.zip`;
}

/**
 * Build and download the full account backup ZIP.
 *
 * @param {{ companyId: string, companyName?: string, currency?: string }} opts
 * @returns {Promise<{ generatedAt: Date, filename: string, taskCount: number, activeTaskCount: number }>}
 */
export async function downloadAccountBackup({ companyId, companyName, currency }) {
  if (!companyId) throw new Error('Missing company context.');

  // Fetch every account-owned dataset in parallel, all tenant-scoped.
  const [
    tasks, logs, users, userRates, floorWorkers, floorWorkerRates,
  ] = await Promise.all([
    fetchCompanyDocs(COLLECTIONS.PRODUCTION_TASKS, companyId),
    fetchCompanyDocs(COLLECTIONS.ACTIVITY_LOGS, companyId),
    fetchCompanyDocs(COLLECTIONS.USERS, companyId),
    fetchCompanyDocs(COLLECTIONS.USER_PRIVATE_RATES, companyId),
    fetchCompanyDocs(COLLECTIONS.FLOOR_WORKERS, companyId),
    fetchCompanyDocs(COLLECTIONS.FLOOR_WORKER_RATES, companyId),
  ]);

  // adminSettings is a single doc whose ID equals the companyId (the Firestore
  // rule authorizes it by doc ID, not a field), so read it directly by ID.
  let adminSettings = null;
  try {
    const adminSnap = await getDoc(doc(db, COLLECTIONS.ADMIN_SETTINGS, companyId));
    adminSettings = adminSnap.exists() ? adminSnap.data() : null;
  } catch {
    adminSettings = null;
  }

  const activeTaskCount = tasks.filter((t) => t.status === 'active').length;
  const generatedAt = new Date();
  const cur = currency || adminSettings?.labourCostCurrency || 'CAD';

  const files = [
    { name: 'production-tasks.csv', content: BOM + buildProductionTasksCSV(tasks, cur) },
    { name: 'worker-performance.csv', content: BOM + buildWorkerPerformanceCSV(tasks, cur) },
    { name: 'audit-log.csv', content: BOM + buildAuditLogCSV(logs) },
    { name: 'employees.csv', content: BOM + buildEmployeesCSV(users, floorWorkers) },
    { name: 'labour-rates.csv', content: BOM + buildLabourRatesCSV({ users, userRates, floorWorkers, floorWorkerRates, adminSettings }) },
    { name: 'account-info.csv', content: BOM + buildAccountInfoCSV({
      companyName: companyName || '', companyId, generatedAt,
      taskCount: tasks.length, activeTaskCount, auditCount: logs.length,
      userCount: users.length, floorWorkerCount: floorWorkers.length,
    }) },
  ];

  const filename = backupFilename(companyName, generatedAt);
  downloadBlob(filename, makeZip(files));

  return { generatedAt, filename, taskCount: tasks.length, activeTaskCount };
}
